/**
 * Edit-mode commit hook for AutoUI variant swatches.
 *
 * AutoUI (trace-viewer runtime) writes swatch picks via `Properties.setValue` — a
 * runtime-tier override that animates but does NOT touch authored data. That's
 * right in PLAY mode, but in EDIT mode it diverges from the behaviors panel:
 * thumbnails don't recapture, the pick isn't undoable, and (when a state is
 * active) it doesn't land in the state. This hook closes that gap.
 *
 * Installed once via `setAutoUISwatchEditorCommit`. When AutoUI is in edit mode it
 * calls this instead of `setValue`; the pick then routes through the SAME editor
 * path the behaviors panel and SlotVariantsRow use:
 *
 * - Material-bound variant property (the MVP case) → `setMaterialColorForState`,
 *   which captures into the active state or the declaration base, regenerates the
 *   slot preview, and records undo.
 * - Any other color property → `captureStateValue` (active state) with a base
 *   fallback handled by the editor.
 */

import type { SceneObject } from 'trace-viewer/scene';
import {
    getMaterialVariantSet,
    setMaterialColorForState,
    captureStateValue,
} from 'trace-viewer/editor';
import { setAutoUISwatchEditorCommit, Renderer } from 'trace-viewer/behaviors';
import type { PropertyValue } from 'trace-viewer/behaviors';

/** 6-digit `#rrggbb` → applied `#rrggbbaa` (opaque), matching handleColorSelect. */
function toAppliedColor(hex: string): string {
    return hex.length === 7 ? hex + 'FF' : hex;
}

/** Find the material slot whose variant set declares `propName`, or null. */
function findVariantSlot(source: SceneObject, propName: string): number | null {
    // Variant sets bind a slot's baseColorFactor; scan the renderer's slots.
    const renderer = source.getBehavior(Renderer);
    const materials = renderer?.getData().materials ?? [];
    const slots = new Set<number>(materials.map(m => (m.targetMaterialIndex ?? 0)));
    // Slot 0 is always a candidate even with no explicit material entry.
    slots.add(0);
    for (const slot of slots) {
        const set = getMaterialVariantSet(source, slot);
        if (set?.propertyName === propName) return slot;
    }
    return null;
}

export function installAutoUISwatchEditorCommit(): void {
    setAutoUISwatchEditorCommit((source: SceneObject, propName: string, value: PropertyValue) => {
        if (typeof value !== 'string') return; // variant values are hex strings
        const slot = findVariantSlot(source, propName);
        if (slot !== null) {
            void setMaterialColorForState(source, toAppliedColor(value), { slot })
                .then(({ errors }) => {
                    if (errors.length > 0) console.warn('[AutoUISwatch] set material color failed:', errors);
                });
            return;
        }
        // Non-material color property: capture into the active state.
        // captureStateValue returns captured:false at Base, or when it refuses
        // the edit (Play), so nothing is committed.
        void captureStateValue(source, propName, value).then(({ captured }) => {
            if (!captured) {
                console.warn(`[AutoUISwatch] "${propName}" not captured (not material-bound, and no active state or the edit was refused in Play)`);
            }
        });
    });
}
